<script>
  import CameraCapture from '../CameraCapture/index.svelte';

  // Multi-image uploader.
  //
  // - `existingImages` are images already saved on the record: an array of
  //   { id, url } objects. They render first and the parent is told which
  //   ones the user removed via `onRemoveExisting(id)`.
  // - New files (chosen or captured) are collected locally and reported to
  //   the parent through `onChange(files)` as a plain array of File objects.
  const {
    label = 'Images',
    required = false,
    error: propError = '',
    existingImages = [],
    onChange = (_files) => {},
    onRemoveExisting = (_id) => {}
  } = $props();

  let showCamera = $state(false);
  // Each entry: { file, url } so we can preview new uploads before submit.
  let newImages = $state([]);
  let fileInputRef;
  let localError = $state('');

  // The cover image is the first one shown — existing images first, then new.
  const hasAnyImage = $derived(existingImages.length > 0 || newImages.length > 0);

  function emitChange() {
    onChange(newImages.map((entry) => entry.file));
  }

  function addFile(file) {
    if (!file || !file.type.startsWith('image/')) {
      localError = 'Selected file is not an image.';
      return;
    }
    localError = '';
    const reader = new FileReader();
    reader.onload = (e) => {
      newImages = [...newImages, { file, url: e.target.result }];
      emitChange();
    };
    reader.readAsDataURL(file);
  }

  function handleFileChange(event) {
    const files = Array.from(event.target.files || []);
    files.forEach(addFile);
    // Allow re-selecting the same file later.
    if (fileInputRef) fileInputRef.value = '';
  }

  function handleCapturedPhoto(captured) {
    const { file, url } = captured;
    localError = '';
    newImages = [...newImages, { file, url }];
    emitChange();
    showCamera = false;
  }

  function removeNewImage(index) {
    newImages = newImages.filter((_, i) => i !== index);
    emitChange();
  }

  function removeExistingImage(id) {
    onRemoveExisting(id);
  }

  function toggleCamera() {
    showCamera = !showCamera;
  }
</script>

<div class="image-uploader">
  <span class="block text-sm font-medium text-gray-700 mb-1">
    {label} {required ? '*' : ''}
  </span>

  {#if hasAnyImage}
    <div class="grid grid-cols-3 gap-2 mb-2">
      {#each existingImages as image (image.id)}
        <div class="relative aspect-square">
          <img
            src={image.url}
            alt="Saved"
            class="w-full h-full object-cover rounded border border-gray-300"
          />
          <button
            type="button"
            class="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white p-1 rounded-full"
            onclick={() => removeExistingImage(image.id)}
            title="Remove image"
            aria-label="Remove image"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      {/each}

      {#each newImages as image, index (image.url)}
        <div class="relative aspect-square">
          <img
            src={image.url}
            alt="Preview"
            class="w-full h-full object-cover rounded border border-brand-300"
          />
          <button
            type="button"
            class="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white p-1 rounded-full"
            onclick={() => removeNewImage(index)}
            title="Remove image"
            aria-label="Remove image"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      {/each}
    </div>
  {:else}
    <div class="mb-2 w-full h-32 bg-gray-100 flex items-center justify-center rounded border border-gray-300 text-gray-400">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    </div>
  {/if}

  <p class="text-xs text-gray-500 mb-1">The first image is used as the thumbnail.</p>

  <div class="flex space-x-2">
    <div class="relative flex-1">
      <input
        type="file"
        bind:this={fileInputRef}
        onchange={handleFileChange}
        accept="image/*"
        multiple
        class="hidden"
        id="file-upload"
      />
      <label
        for="file-upload"
        class="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
      >
        Choose Files
      </label>
    </div>

    <button
      type="button"
      class="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand-700 hover:bg-brand-800"
      onclick={toggleCamera}
    >
      {showCamera ? 'Hide Camera' : 'Take Photo'}
    </button>
  </div>

  {#if propError || localError}
    <p class="mt-1 text-sm text-red-600">{propError || localError}</p>
  {/if}

  {#if showCamera}
    <div class="mt-3 border rounded-lg overflow-hidden">
      <CameraCapture
        onImageCaptured={handleCapturedPhoto}
        onImageAccepted={handleCapturedPhoto}
      />
    </div>
  {/if}
</div>
